/**
 * HeaderDrawer - Light Theme Sidebar Header
 * 
 * Eye-friendly design with:
 * - Clean avatar styling
 * - Soft borders
 * - Muted text colors
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes } from '@emotion/react';
import { DrawerHeader } from '../SideMenu/SideMenuTheme';
import { useSelector } from 'react-redux';
import Cookies from 'universal-cookie';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import '../styles/ModernStyles.css';

const cookies = new Cookies();

// Animation keyframes
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
`;

export default function HeaderDrawer({ open, setOpen, handleDrawerOpen, handleDrawerClose, getUserImage }) {
  const drawerwidth = useSelector(state => state.settingsData.drawerwidth);
  const sidemenutheme = useSelector(state => state.themeData.sidemenutheme);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Toggle Button */}
      <Box
        onClick={open ? handleDrawerClose : handleDrawerOpen}
        sx={{
          position: 'absolute',
          top: !open ? '12px' : '1px',
          left: !open ? '50%' : '90%',
          transform: !open ? 'translateX(-50%)' : 'translateX(-90%)',
          width: 32,
          height: 32,
          borderRadius: 2,
          background: 'rgba(78, 205, 196, 0.08)',
          border: '1px solid rgba(78, 205, 196, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'rgba(78, 205, 196, 0.15)',
            borderColor: '#4ECDC4',
          },
        }}
      >
        {!open ? (
          <ChevronLeftIcon sx={{ color: '#4ECDC4', fontSize: 20 }} />
        ) : (
          <ChevronRightIcon sx={{ color: '#4ECDC4', fontSize: 20 }} />
        )}
      </Box>

      {/* User Info Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: open ? 'center' : 'flex-end',
          alignItems: 'center',
          marginBottom: '10px',
          width: '100%',
          height: open ? '180px' : '100px',
          paddingTop: open ? '40px' : '50px',
        }}
      >
        {/* Avatar */}
        <Box
          sx={{
            position: 'relative',
            marginBottom: open ? 2 : 0,
          }}
        >
          <Box
            component="img"
            src={getUserImage()}
            alt="User Avatar"
            sx={{
              width: open ? 75 : 42,
              height: open ? 75 : 42,
              borderRadius: '50%',
              objectFit: 'cover',
              border: '3px solid #E2E8F0',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              '&:hover': {
                borderColor: '#4ECDC4',
              },
            }}
          />
          {/* Online Indicator */}
          <Box
            sx={{
              position: 'absolute',
              bottom: open ? 4 : 2,
              right: open ? 4 : 2,
              width: open ? 14 : 10,
              height: open ? 14 : 10,
              borderRadius: '50%',
              background: '#10B981',
              border: '2px solid #FFFFFF',
              boxShadow: '0 0 6px rgba(16, 185, 129, 0.4)',
            }}
          />
        </Box>

        {/* Account Name */}
        {open && (
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 600,
              color: '#1E293B',
              fontFamily: 'Cairo, sans-serif',
              animation: `${fadeIn} 0.3s ease`,
              textAlign: 'center',
              maxWidth: '90%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cookies.get('account_name') || 'Account'}
          </Typography>
        )}

        {/* Username */}
        {open && (
          <Typography
            sx={{
              fontSize: 13,
              color: '#64748B',
              fontFamily: 'Cairo, sans-serif',
              marginTop: 0.5,
              animation: `${fadeIn} 0.3s ease 0.1s both`,
              textAlign: 'center',
              maxWidth: '90%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            @{cookies.get('user_name') || 'user'}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
